import { Fragment } from 'react';
import { NovelScriptStateProvider } from '~/app/(default-layout)/s/[storyId]/_containers/novel-script-render/novel-script-state-provider';
import { ReadEventContainer } from '~/app/(default-layout)/s/[storyId]/_containers/novel-script-render/read-event-container';
import type { StoryOverview } from '~/common/types/story-overview';
import { InReadAdContainer } from '@app/(default-layout)/s/[storyId]/_containers/in-read-ad-container';
import { Box, styled } from '@styled-system/jsx';
import { splitParagraphs } from './utils/split-paragraphs';

type Props = {
  story: StoryOverview;
  novelScript: string;
  canShowGoogleAd: boolean;
  nextStoryId?: string;
  nextStorySeriesIndex?: number;
  nextStoryIsVipOnly?: boolean;
};

export const NovelScriptRender = ({
  novelScript,
  story,
  canShowGoogleAd,
  nextStoryId,
  nextStorySeriesIndex,
  nextStoryIsVipOnly,
}: Props) => {
  const splitChunkSize = 30;
  const chunkedNovelScript = splitParagraphs(novelScript, splitChunkSize);
  return (
    <NovelScriptStateProvider story={story}>
      <ReadEventContainer
        nextStoryId={nextStoryId}
        nextStorySeriesIndex={nextStorySeriesIndex}
        nextStoryIsVipOnly={nextStoryIsVipOnly}
      >
        <NovelBody>
          {chunkedNovelScript.map((chunkedHtml, index) => (
            <Fragment key={`chunk-${index}`}>
              <Box
                key={index}
                dangerouslySetInnerHTML={{ __html: chunkedHtml }}
              />
              {index < chunkedNovelScript.length - 1 && (
                <InReadAdContainer
                  canShowGoogleAd={canShowGoogleAd}
                  adIndex={index}
                  my="10px"
                />
              )}
            </Fragment>
          ))}
        </NovelBody>
      </ReadEventContainer>
    </NovelScriptStateProvider>
  );
};

const NovelBody = styled('div', {
  base: {
    alignSelf: 'flex-start',
    height: '100%',
    fontFamily:
      "'Hiragino Sans', 'Hiragino Kaku Gothic Pro', 'ヒラギノ角ゴ Pro W3', Meiryo, メイリオ, Osaka, 'MS PGothic', arial, helvetica, sans-serif",
    fontSize: 'size_16',
    flex: 1,
    overflowY: 'auto',
    width: '100%',
    color: 'accent.1000',
    lineHeight: '1.5',
    background: '#fafafa',
    WebkitTouchCallout: 'none',
    WebkitUserModify: 'none',
    userSelect: 'none',
    '& p': {
      marginBottom: '12px',
    },
    '& u': {
      textUnderlineOffset: '3px',
    },
    '& h1': {
      fontSize: 'size_32',
      fontWeight: '700',
    },
    '& h1, h2, h3, h4, h5, h6': {
      margin: '0.67em 0',
    },
    '& .font-size-large': {
      fontSize: '24px',
    },
    '& .font-size-large:is(h1, h2, h3, h4, h5, h6)': {
      fontSize: '29px',
    },
    '& .font-size-large:webkit-any(h1, h2, h3, h4, h5, h6)': {
      fontSize: '29px',
    },
    '& .font-size-medium': {
      fontSize: '17px',
    },
    '& .font-size-medium:is(h1, h2, h3, h4, h5, h6)': {
      fontSize: '22px',
    },
    '& .font-size-medium:webkit-any(h1, h2, h3, h4, h5, h6)': {
      fontSize: '22px',
    },
    '& .font-size-small': {
      fontSize: '14px',
    },
    '& .font-size-small:is(h1, h2, h3, h4, h5, h6)': {
      fontSize: '19px',
    },
    '& .font-size-small:webkit-any(h1, h2, h3, h4, h5, h6)': {
      fontSize: '19px',
    },
    '& img': {
      maxWidth: '90%',
      maxHeight: '500px',
      display: 'block',
      margin: 'auto',
      padding: '24px',
      marginBottom: '12px',
    },
    '& em': {
      fontFamily: "helvetica, 'serif', sans-serif",
      fontStyle: 'italic',
    },
    '& rp': {
      fontSize: 'size_16',
    },
    '& rt': {
      fontSize: 'size_8',
    },
    '& hr': {
      height: '20px',
      padding: '0 20px',
      textAlign: 'center',
      border: '0',
      _before: {
        content: `url("data:image/svg+xml,%3Csvg width='53' height='68' viewBox='0 0 53 68' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='2.5' cy='34' r='2' fill='%2362646E'/%3E%3Ccircle cx='26.5' cy='34' r='2' fill='%2362646E'/%3E%3Ccircle cx='50.5' cy='34' r='2' fill='%2362646E'/%3E%3C/svg%3E%0A")`,
        position: 'relative',
        top: '-20px',
      },
    },
  },
});
